import React, { useState, useCallback } from "react";
import { View, StyleSheet, Platform, ViewStyle } from "react-native";
import CustomText from "../CustomText/CustomText";
import CustomButton from "../CustomButton/CustomButton";
import { File, X, FileText } from "@phosphor-icons/react/dist/ssr";

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const DEFAULT_ACCEPTED_TYPES = [".pdf", ".doc", ".docx", ".txt", ".zip", ".jpg", ".jpeg", ".png"];

interface ValidationError {
  message: string;
  code: "size" | "type" | "count";
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  fileId?: string; // ID from backend after successful upload
  uploadStatus?: "pending" | "uploading" | "completed" | "error";
  file?: File; // original file object for web
}

const validateFile = (file: File, acceptedTypes: string[], maxSize: number): ValidationError | null => {
  // Check file size
  if (file.size > maxSize) {
    return {
      message: `File size should not exceed ${Math.round(maxSize / (1024 * 1024))}MB`,
      code: "size",
    };
  }

  // Check file type
  const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
  const isAccepted = acceptedTypes.some((type) => file.type.includes(type.replace(".", "")) || fileExtension === type);

  if (!isAccepted) {
    return {
      message: `Only ${acceptedTypes.join(", ")} files are allowed`,
      code: "type",
    };
  }

  return null;
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const getFileIcon = (fileName: string) => {
  const extension = fileName.split(".").pop()?.toLowerCase();
  const iconProps = { size: 20, color: "#667085" };

  switch (extension) {
    case "pdf":
      return <FileText {...iconProps} color="#DC2626" />;
    case "doc":
    case "docx":
      return <FileText {...iconProps} color="#2563EB" />;
    case "jpg":
    case "jpeg":
    case "png":
      return <FileText {...iconProps} color="#059669" />;
    case "zip":
      return <FileText {...iconProps} color="#7C2D12" />;
    default:
      return <File {...iconProps} />;
  }
};

interface DocumentUploadProps {
  onFilesSelected: (files: UploadedFile[]) => void;
  acceptedTypes?: string[];
  maxFiles?: number;
  maxFileSize?: number; // in MB
  multiple?: boolean;
  containerStyle?: ViewStyle;
  buttonStyle?: ViewStyle;
  buttonText?: string;
  dropzoneText?: string;
  dropzoneSubtext?: string;
  onError?: (error: ValidationError) => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onFilesSelected,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  maxFiles = 5,
  maxFileSize = 50, // MB
  multiple = true,
  containerStyle,
  buttonStyle,
  buttonText = "Select Documents",
  dropzoneText = "Drag & drop files or click to browse",
  dropzoneSubtext,
  onError,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const maxSizeBytes = maxFileSize * 1024 * 1024;
  const defaultSubtext = dropzoneSubtext || `${acceptedTypes.join(", ")} format, Max file size: ${maxFileSize}MB`;

  const processFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const files = Array.from(fileList);
      const newFiles: UploadedFile[] = [];

      // Check if adding these files would exceed max count
      if (uploadedFiles.length + files.length > maxFiles) {
        onError?.({
          message: `Maximum ${maxFiles} files allowed`,
          code: "count",
        });
        return;
      }

      for (const file of files) {
        const error = validateFile(file, acceptedTypes, maxSizeBytes);
        if (error) {
          onError?.(error);
          continue;
        }

        const uploadedFile: UploadedFile = {
          id: Math.random().toString(36).substr(2, 9),
          name: file.name,
          size: file.size,
          type: file.type,
          file: file,
          uploadStatus: "pending",
        };

        newFiles.push(uploadedFile);
      }

      // Add files to state
      const updatedFiles = [...uploadedFiles, ...newFiles];
      setUploadedFiles(updatedFiles);

      // Call onFilesSelected with the updated files
      onFilesSelected(updatedFiles);
    },
    [uploadedFiles, maxFiles, acceptedTypes, maxSizeBytes, onError, onFilesSelected]
  );

  const removeFile = useCallback(
    (fileId: string) => {
      const updatedFiles = uploadedFiles.filter((file) => file.id !== fileId);
      setUploadedFiles(updatedFiles);
      onFilesSelected(updatedFiles);
    },
    [uploadedFiles, onFilesSelected]
  );

  const handleFileSelection = useCallback(async () => {
    if (Platform.OS !== "web") {
      // For native platforms, we'd need to use document picker
      console.log("Document picker not implemented for native platforms");
      return;
    }
  }, []);

  if (Platform.OS !== "web") {
    return (
      <View style={[styles.mobileUploadContainer, containerStyle]}>
        <CustomButton
          text={buttonText}
          onPress={handleFileSelection}
          styleType="secondary"
          size="m"
          style={[styles.uploadButton, buttonStyle]}
          textStyle={styles.uploadButtonText}
          leftIcon={null}
          rightIcon={null}
          disabled={false}
          textProps={{}}
        />
      </View>
    );
  }

  const dropZoneStyle = {
    position: "relative" as const,
    width: "100%",
    minHeight: "120px",
    border: `2px dashed ${isDragging ? "#3E68FE" : "#D1D5DB"}`,
    borderRadius: "6px",
    backgroundColor: isDragging ? "#EEF2FF" : "#F9FAFB",
    display: "flex" as const,
    flexDirection: "column" as const,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    padding: "16px",
    cursor: isUploading ? "not-allowed" : "pointer",
    opacity: isUploading ? 0.7 : 1,
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "uploading":
        return "#F59E0B";
      case "completed":
        return "#10B981";
      case "error":
        return "#EF4444";
      default:
        return "#6B7280";
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case "uploading":
        return "Uploading...";
      case "completed":
        return "Uploaded";
      case "error":
        return "Failed";
      default:
        return "Pending";
    }
  };

  return (
    <View style={styles.container}>
      <div
        style={dropZoneStyle}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);

          if (!isUploading) {
            const files = e.dataTransfer?.files;
            if (files && files.length > 0) {
              processFiles(files);
            }
          }
        }}>
        <File size={32} color="#9CA3AF" style={{ marginBottom: 8 }} />
        <CustomText
          text={isUploading ? "Uploading files..." : dropzoneText}
          useTranslationText={false}
          size="s"
          style={styles.dropZoneText}
        />
        <CustomText text={defaultSubtext} useTranslationText={false} size="xs" style={styles.dropZoneSubtext} />

        <input
          id="file-input"
          type="file"
          accept={acceptedTypes.join(",")}
          multiple={multiple}
          disabled={isUploading}
          onChange={(e) => {
            const files = e.target.files;
            if (files && files.length > 0) {
              processFiles(files);
            }
            // Reset input to allow selecting same file again
            e.target.value = "";
          }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0,
            width: "100%",
            height: "100%",
            cursor: isUploading ? "not-allowed" : "pointer",
            zIndex: 1,
          }}
        />
      </div>

      {/* File List */}
      {uploadedFiles.length > 0 && (
        <View style={styles.fileList}>
          <CustomText
            text={`${uploadedFiles.length} file${uploadedFiles.length > 1 ? "s" : ""} selected`}
            useTranslationText={false}
            size="s"
            weight="bold"
            style={styles.fileListHeader}
          />
          {uploadedFiles.map((file) => (
            <View key={file.id} style={styles.fileItem}>
              <View style={styles.fileInfo}>
                <View style={styles.fileIcon}>{getFileIcon(file.name)}</View>
                <View style={styles.fileDetails}>
                  <CustomText text={file.name} useTranslationText={false} size="s" style={styles.fileName} />
                  <View style={styles.fileMetadata}>
                    <CustomText
                      text={formatFileSize(file.size)}
                      useTranslationText={false}
                      size="xs"
                      style={styles.fileSize}
                    />
                    <CustomText text=" • " useTranslationText={false} size="xs" style={styles.fileSize} />
                    <CustomText
                      text={getStatusText(file.uploadStatus)}
                      useTranslationText={false}
                      size="xs"
                      style={[styles.fileStatus, { color: getStatusColor(file.uploadStatus) }]}
                    />
                  </View>
                </View>
              </View>
              <CustomButton
                text=""
                onPress={() => removeFile(file.id)}
                styleType="ghost"
                size="xs"
                style={styles.removeButton}
                leftIcon={<X size={16} color="#DC2626" />}
                textProps={{}}
                disabled={file.uploadStatus === "uploading"}
              />
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  mobileUploadContainer: {
    width: "100%",
    alignItems: "center",
    gap: 12,
  },
  uploadButton: {
    width: "100%",
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  uploadButtonText: {
    color: "#111827",
  },
  dropZoneText: {
    color: "#374151",
    marginBottom: 4,
    textAlign: "center",
  },
  dropZoneSubtext: {
    color: "#6B7280",
    textAlign: "center",
  },
  fileList: {
    marginTop: 16,
    padding: 12,
    backgroundColor: "#F9FAFB",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  fileListHeader: {
    color: "#374151",
    marginBottom: 8,
  },
  fileItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "#FFFFFF",
    borderRadius: 4,
    marginBottom: 4,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  fileInfo: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  fileIcon: {
    marginRight: 12,
  },
  fileDetails: {
    flex: 1,
  },
  fileName: {
    color: "#374151",
    marginBottom: 2,
  },
  fileMetadata: {
    flexDirection: "row",
    alignItems: "center",
  },
  fileSize: {
    color: "#6B7280",
  },
  fileStatus: {
    fontWeight: "500",
  },
  removeButton: {
    padding: 4,
  },
});

export default DocumentUpload;
